import {
  CollectionProgressItem,
  CollectionStatus,
  Money as MoneyModel,
  TrackRecordProject,
} from 'api-client';
import { declension } from 'utils';

/**
 * Проект в работе, пока в работе хотя бы одна его строка реестра.
 */
const getProjectStatus = (
  items: ReadonlyArray<CollectionProgressItem>
): CollectionStatus | undefined => {
  if (items.some((item) => item.status === CollectionStatus.OPEN)) {
    return CollectionStatus.OPEN;
  }

  return items.every((item) => item.status === CollectionStatus.CLOSED)
    ? CollectionStatus.CLOSED
    : undefined;
};

/**
 * Сумма по строкам реестра. undefined, если хоть одной суммы нет или валюты разные —
 * лучше прочерк, чем неполное число.
 */
const sumMoney = (values: ReadonlyArray<MoneyModel | undefined>): MoneyModel | undefined => {
  if (values.length === 0 || values.some((value) => value === undefined)) {
    return undefined;
  }

  const [first] = values as ReadonlyArray<MoneyModel>;
  if (
    (values as ReadonlyArray<MoneyModel>).some((value) => value.currencyCode !== first.currencyCode)
  ) {
    return undefined;
  }

  return new MoneyModel(
    (values as ReadonlyArray<MoneyModel>).reduce((total, value) => total + value.amount, 0),
    first.currencyCode
  );
};

/**
 * Достижение целевой суммы — значение из реестра, а не пересчёт. Если у проекта
 * несколько строк реестра, своего показателя на весь проект в реестре нет, и он виден
 * по каждой строке в раскрытой строке таблицы.
 */
const getAchievement = (items: ReadonlyArray<CollectionProgressItem>): number | undefined =>
  items.length === 1 && items[0].targetAchievementPercent != null
    ? items[0].targetAchievementPercent
    : undefined;

/** Отрицательное "Время взыскания" встречается в реестре — это ошибка данных, не срок. */
export const getValidDays = (item: CollectionProgressItem): number | undefined =>
  item.collectionDays != null && item.collectionDays >= 0 ? item.collectionDays : undefined;

/**
 * Время взыскания по проекту — самое долгое из его строк реестра, если оно известно
 * по каждой строке; иначе прочерк.
 */
const getCollectionDays = (items: ReadonlyArray<CollectionProgressItem>): number | undefined => {
  const days = items.map(getValidDays);
  if (days.length === 0 || days.some((value) => value === undefined)) {
    return undefined;
  }

  return Math.max(...(days as ReadonlyArray<number>));
};

/** Плановая дата взыскания по проекту — самая поздняя из его строк реестра. */
const getFinishPlannedAt = (items: ReadonlyArray<CollectionProgressItem>): string | undefined =>
  items
    .map((item) => item.finishPlannedAt)
    .filter((date): date is string => Boolean(date))
    .sort()
    .pop();

export type Row = {
  project: TrackRecordProject;
  status?: CollectionStatus;
  targetSum?: MoneyModel;
  paidSum?: MoneyModel;
  plannedSum?: MoneyModel;
  /** Доля: 1.1775 = 117,75 %. */
  achievement?: number;
  finishPlannedAt?: string;
  collectionDays?: number;
  /** В работе, но платежей по графику больше не запланировано. */
  nothingPlanned: boolean;
};

export const toRow = (project: TrackRecordProject): Row => {
  const status = getProjectStatus(project.items);
  const plannedSum = sumMoney(project.items.map((item) => item.plannedSum));

  return {
    project,
    status,
    targetSum: sumMoney(project.items.map((item) => item.targetSum)),
    paidSum: sumMoney(project.items.map((item) => item.paidSum)),
    plannedSum,
    achievement: getAchievement(project.items),
    finishPlannedAt: getFinishPlannedAt(project.items),
    collectionDays: getCollectionDays(project.items),
    nothingPlanned: status === CollectionStatus.OPEN && plannedSum?.amount === 0,
  };
};

export const projectTitle = (project: TrackRecordProject): string =>
  project.projectName ?? 'Проект без названия';

// Форматы

/** Доля с одним знаком после запятой: 73,2 %, 100,0 % — одинаковая ширина в колонке. */
export const formatShare = (share: number): string =>
  `${(share * 100).toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}\xa0%`;

/** Сумма в копейках — в миллионах рублей: 4 347,9 млн ₽. */
export const formatMillions = (kopecks: number): string =>
  `${(kopecks / 100 / 1_000_000).toLocaleString('ru-RU', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}\xa0млн\xa0₽`;

export const formatDays = (days: number): string =>
  `${days}\xa0${declension(days, ['день', 'дня', 'дней'])}`;

// Итоги

const median = (values: ReadonlyArray<number>): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 ? sorted[middle] : Math.round((sorted[middle - 1] + sorted[middle]) / 2);
};

export type Summary = {
  total: number;
  closed: number;
  /** Поступило ÷ целевая по проектам, где обе суммы есть и они в рублях. */
  paidShare?: { paid: number; target: number };
  /** Завершённые взыскания (строки реестра) с достижением ≥ 100 %. */
  reachedTarget?: { reached: number; measured: number };
  /** Медиана "Времени взыскания" по завершённым взысканиям. */
  medianDays?: { days: number; measured: number };
};

/**
 * Итоги по всем проектам, независимо от фильтра и поиска. Достижение и время считаются
 * по строкам реестра, а не по проектам: у проекта из нескольких строк своего значения нет.
 */
export const summarize = (rows: ReadonlyArray<Row>): Summary => {
  const closedItems = rows.flatMap((row) =>
    row.project.items.filter((item) => item.status === CollectionStatus.CLOSED)
  );

  const summed = rows.filter(
    (row) => row.targetSum?.currencyCode === 'RUB' && row.paidSum?.currencyCode === 'RUB'
  );
  const target = summed.reduce((total, row) => total + (row.targetSum as MoneyModel).amount, 0);
  const paid = summed.reduce((total, row) => total + (row.paidSum as MoneyModel).amount, 0);

  const achievements = closedItems
    .map((item) => item.targetAchievementPercent)
    .filter((value): value is number => value != null);

  const days = closedItems
    .map(getValidDays)
    .filter((value): value is number => value !== undefined);

  return {
    total: rows.length,
    closed: rows.filter((row) => row.status === CollectionStatus.CLOSED).length,
    paidShare: target > 0 ? { paid, target } : undefined,
    reachedTarget: achievements.length
      ? {
          reached: achievements.filter((value) => value >= 1).length,
          measured: achievements.length,
        }
      : undefined,
    medianDays: days.length ? { days: median(days), measured: days.length } : undefined,
  };
};

// Фильтр, поиск и сортировка — в строке запроса: ?status=closed&sort=days&dir=desc&q=559

export type StatusFilter = 'all' | CollectionStatus;

const STATUS_PARAMS: Record<string, CollectionStatus> = {
  open: CollectionStatus.OPEN,
  closed: CollectionStatus.CLOSED,
};

export const parseStatus = (value: string | null): StatusFilter =>
  (value && STATUS_PARAMS[value]) || 'all';

export const statusParam = (filter: StatusFilter): string | undefined =>
  filter === 'all' ? undefined : filter.toLowerCase();

export type SortKey = 'target' | 'paid' | 'achievement' | 'finish' | 'days';
export type SortDirection = 'asc' | 'desc';
export type Sort = { key: SortKey; direction: SortDirection };

const SORT_VALUES: Record<SortKey, (row: Row) => number | string | undefined> = {
  target: (row) => row.targetSum?.amount,
  paid: (row) => row.paidSum?.amount,
  achievement: (row) => row.achievement,
  finish: (row) => row.finishPlannedAt,
  days: (row) => row.collectionDays,
};

export const parseSort = (key: string | null, direction: string | null): Sort | undefined =>
  key && key in SORT_VALUES
    ? { key: key as SortKey, direction: direction === 'asc' ? 'asc' : 'desc' }
    : undefined;

/** Первый клик — по убыванию, второй — по возрастанию, третий — обратно к порядку по номеру. */
export const nextSort = (current: Sort | undefined, key: SortKey): Sort | undefined => {
  if (current?.key !== key) {
    return { key, direction: 'desc' };
  }

  return current.direction === 'desc' ? { key, direction: 'asc' } : undefined;
};

/**
 * Без сортировки — порядок crowdfunding (по номеру проекта по убыванию). Пустые значения
 * всегда в конце, в любом направлении; равные сохраняют исходный порядок.
 */
export const sortRows = (rows: ReadonlyArray<Row>, sort: Sort | undefined): ReadonlyArray<Row> => {
  if (!sort) {
    return rows;
  }

  const value = SORT_VALUES[sort.key];
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const left = value(a);
    const right = value(b);
    if (left === undefined || right === undefined) {
      return left === right ? 0 : left === undefined ? 1 : -1;
    }

    return left < right ? -factor : left > right ? factor : 0;
  });
};

const normalize = (value: string): string => value.toLowerCase().replace(/ё/g, 'е');

/** Поиск по названию или номеру: "559", "№559" и "стройинд" находят №559. */
export const matchesQuery = (row: Row, query: string): boolean => {
  const needle = normalize(query.trim().replace(/^[№#]\s*/, ''));
  if (!needle) {
    return true;
  }

  const number = row.project.projectNumber;
  if (/^\d+$/.test(needle) && number !== undefined && String(number).startsWith(needle)) {
    return true;
  }

  return normalize(projectTitle(row.project)).includes(needle);
};
